/**
 * Coverage status of each Argentine jurisdiction in SICA's Curricular Asset Inventory (IAC).
 *
 * Decision 12.4 (ADR-005 §7): all of Argentina is in scope. At launch most jurisdictions
 * are in "pendiente" state; the map honestly shows what is and isn't covered.
 *
 * The `gridPosition` field places each jurisdiction on a 5×10 schematic grid that
 * approximates Argentina's geography (north→south rows, west→east columns). The
 * negative space of the grid is part of the design: it draws the country's silhouette.
 *
 * TODO(12.4): wire to backend IAC. This file is the launch placeholder.
 */

export type CoverageStatus = 'covered' | 'progress' | 'pending';

export type GridPosition = {
  /** 0 = north, 9 = south */
  row: number;
  /** 0 = west, 4 = east */
  col: number;
};

export type Jurisdiction = {
  id: string;
  /** 3–4 letter canonical code shown inside the map cell (e.g. JUJ, BSAS, CABA). */
  code: string;
  name: string;
  status: CoverageStatus;
  region: 'NOA' | 'NEA' | 'Cuyo' | 'Centro' | 'Patagonia' | 'CABA';
  gridPosition: GridPosition;
  designTitle?: string;
  version?: string;
  validatedAt?: string;
  levels?: ReadonlyArray<'inicial' | 'primaria' | 'secundaria'>;
};

export const MAP_GRID_COLS = 5;
export const MAP_GRID_ROWS = 10;

/**
 * The "Nacional" entity is not a jurisdiction; it is the normative layer (NAP).
 * Listed here for completeness in the legend but rendered separately from the map.
 */
export const NATIONAL_NAP = {
  id: 'nacional',
  name: 'NAP Nacional',
  status: 'covered' as const,
  scope: 'Núcleos de Aprendizajes Prioritarios — todas las áreas, todos los niveles obligatorios.',
};

export const JURISDICTIONS: readonly Jurisdiction[] = [
  // CABA + Centro
  { id: 'caba', code: 'CABA', name: 'Ciudad Autónoma de Buenos Aires', status: 'progress', region: 'CABA', gridPosition: { row: 4, col: 4 }, designTitle: 'Diseño Curricular CABA', version: '2024', validatedAt: '2026-03-15', levels: ['inicial', 'primaria'] },
  { id: 'buenos-aires', code: 'BSAS', name: 'Buenos Aires', status: 'progress', region: 'Centro', gridPosition: { row: 5, col: 3 }, designTitle: 'Diseño Curricular DGCyE', version: '2022', validatedAt: '2026-02-20', levels: ['primaria'] },
  { id: 'cordoba', code: 'CBA', name: 'Córdoba', status: 'pending', region: 'Centro', gridPosition: { row: 4, col: 2 } },
  { id: 'santa-fe', code: 'SFE', name: 'Santa Fe', status: 'pending', region: 'Centro', gridPosition: { row: 3, col: 3 } },
  { id: 'entre-rios', code: 'ERI', name: 'Entre Ríos', status: 'pending', region: 'Centro', gridPosition: { row: 3, col: 4 } },
  { id: 'la-pampa', code: 'LPA', name: 'La Pampa', status: 'pending', region: 'Centro', gridPosition: { row: 5, col: 2 } },

  // NOA
  { id: 'jujuy', code: 'JUJ', name: 'Jujuy', status: 'pending', region: 'NOA', gridPosition: { row: 0, col: 1 } },
  { id: 'salta', code: 'SAL', name: 'Salta', status: 'pending', region: 'NOA', gridPosition: { row: 1, col: 1 } },
  { id: 'tucuman', code: 'TUC', name: 'Tucumán', status: 'pending', region: 'NOA', gridPosition: { row: 2, col: 1 } },
  { id: 'catamarca', code: 'CAT', name: 'Catamarca', status: 'pending', region: 'NOA', gridPosition: { row: 2, col: 0 } },
  { id: 'la-rioja', code: 'LRJ', name: 'La Rioja', status: 'pending', region: 'NOA', gridPosition: { row: 3, col: 0 } },
  { id: 'santiago-del-estero', code: 'SGO', name: 'Santiago del Estero', status: 'pending', region: 'NOA', gridPosition: { row: 3, col: 2 } },

  // NEA
  { id: 'formosa', code: 'FOR', name: 'Formosa', status: 'pending', region: 'NEA', gridPosition: { row: 1, col: 3 } },
  { id: 'chaco', code: 'CHA', name: 'Chaco', status: 'pending', region: 'NEA', gridPosition: { row: 2, col: 3 } },
  { id: 'corrientes', code: 'COR', name: 'Corrientes', status: 'pending', region: 'NEA', gridPosition: { row: 2, col: 4 } },
  { id: 'misiones', code: 'MIS', name: 'Misiones', status: 'pending', region: 'NEA', gridPosition: { row: 1, col: 4 } },

  // Cuyo
  { id: 'mendoza', code: 'MZA', name: 'Mendoza', status: 'pending', region: 'Cuyo', gridPosition: { row: 5, col: 0 } },
  { id: 'san-juan', code: 'SJU', name: 'San Juan', status: 'pending', region: 'Cuyo', gridPosition: { row: 4, col: 0 } },
  { id: 'san-luis', code: 'SLU', name: 'San Luis', status: 'pending', region: 'Cuyo', gridPosition: { row: 5, col: 1 } },

  // Patagonia
  { id: 'neuquen', code: 'NEU', name: 'Neuquén', status: 'pending', region: 'Patagonia', gridPosition: { row: 6, col: 0 } },
  { id: 'rio-negro', code: 'RNE', name: 'Río Negro', status: 'pending', region: 'Patagonia', gridPosition: { row: 6, col: 1 } },
  { id: 'chubut', code: 'CHU', name: 'Chubut', status: 'pending', region: 'Patagonia', gridPosition: { row: 7, col: 1 } },
  { id: 'santa-cruz', code: 'SCR', name: 'Santa Cruz', status: 'pending', region: 'Patagonia', gridPosition: { row: 8, col: 1 } },
  { id: 'tierra-del-fuego', code: 'TDF', name: 'Tierra del Fuego', status: 'pending', region: 'Patagonia', gridPosition: { row: 9, col: 2 } },
];

export const STATUS_LABELS: Record<CoverageStatus, { label: string; description: string }> = {
  covered: {
    label: 'Cubierta',
    description: 'Diseño jurisdiccional ingerido y curado. Disponible para consulta con cita.',
  },
  progress: {
    label: 'En proceso',
    description: 'Documentos ingeridos. Curaduría humana en curso antes de habilitar la consulta.',
  },
  pending: {
    label: 'Pendiente',
    description: 'No ingerido todavía. Avisamos por jurisdicción a medida que la sumamos.',
  },
};

export const REGIONS = ['CABA', 'Centro', 'NOA', 'NEA', 'Cuyo', 'Patagonia'] as const;

export function countByStatus(): Record<CoverageStatus, number> {
  const counts: Record<CoverageStatus, number> = { covered: 0, progress: 0, pending: 0 };
  for (const j of JURISDICTIONS) counts[j.status]++;
  return counts;
}
